import React from 'react';
import { Separator } from '@/components/ui/separator';
import { getFOSStatus, getSteelGroutBond } from '@/lib/calculations';

export const InputSummary = ({ pileInputs, soilLayers, results, designLoads, fos }) => {
  // Guard against undefined inputs
  if (!pileInputs || !soilLayers) {
    return <div className="text-sm text-muted-foreground">No data available</div>;
  }

  // Calculate summary values
  const avgDiameter = pileInputs.pileSections?.length > 0
    ? pileInputs.pileSections.reduce((sum, s) => sum + s.diameter, 0) / pileInputs.pileSections.length
    : 0;

  const totalPileLength = pileInputs.pileSections?.reduce((sum, s) => sum + s.length, 0) || 0;
  const maxDepth = soilLayers.length > 0 ? Math.max(...soilLayers.map(l => l.to)) : 0;
  
  // Get effective steel-grout bond
  const effectiveSteelGroutBond = getSteelGroutBond(pileInputs);
  const bondMode = pileInputs.steelGroutBondMode || 'manual';
  
  // Weighted average q_u
  let avgQu = 0;
  let totalThickness = 0;
  soilLayers.forEach(layer => {
    const thickness = layer.to - layer.from;
    avgQu += layer.qu * thickness;
    totalThickness += thickness;
  });
  avgQu = totalThickness > 0 ? avgQu / totalThickness : 0;

  const summaryItems = [
    { label: 'Pile Diameter', value: `${avgDiameter.toFixed(0)} mm`, testId: 'summary-diameter' },
    { label: 'Pile Length (embedded)', value: `${totalPileLength.toFixed(1)} m`, testId: 'summary-pile-length' },
    { label: 'Steel Grade / Yield', value: `${pileInputs.steelYieldStrength} MPa`, testId: 'summary-steel-grade' },
    { label: 'Grout Length', value: `${pileInputs.groutLength} m`, testId: 'summary-grout-length' },
    { label: 'Grout Strength', value: `${pileInputs.groutStrength} MPa`, testId: 'summary-grout-strength' },
    { label: 'Steel-Grout Bond', value: `${effectiveSteelGroutBond.toFixed(2)} MPa (${bondMode})`, testId: 'summary-steel-grout-bond' },
    { label: 'Hole Oversize', value: `${pileInputs.holeOversize} mm`, testId: 'summary-hole-oversize' },
  ];

  const soilItems = [
    { label: 'Soil Layers', value: soilLayers.length.toString(), testId: 'summary-soil-layers' },
    { label: 'Max Depth', value: `${maxDepth.toFixed(1)} m`, testId: 'summary-max-depth' },
    { label: 'Avg qᵤ', value: `${avgQu.toFixed(2)} MPa`, testId: 'summary-avg-qu' },
  ];

  return (
    <div className="space-y-4" data-testid="input-summary">
      {/* Pile Parameters */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
          Pile Parameters
        </h3>
        <div className="space-y-1">
          {summaryItems.map((item, index) => (
            <div key={index} className="input-summary-item" data-testid={item.testId}>
              <span className="input-summary-label">{item.label}</span>
              <span className="input-summary-value">{item.value}</span>
            </div>
          ))}
        </div>
      </div>

      <Separator />

      {/* Soil Profile */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
          Soil Profile
        </h3>
        <div className="space-y-1">
          {soilItems.map((item, index) => (
            <div key={index} className="input-summary-item" data-testid={item.testId}>
              <span className="input-summary-label">{item.label}</span>
              <span className="input-summary-value">{item.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Results Preview */}
      {results && (
        <>
          <Separator />
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
              Results Preview
            </h3>
            <div className="space-y-1">
              <div className="input-summary-item" data-testid="summary-ultimate-capacity">
                <span className="input-summary-label">Ultimate Capacity</span>
                <span className="input-summary-value text-[#00284F] dark:text-blue-400 font-semibold">
                  {results.ultimateCapacity.toFixed(3)} MN
                </span>
              </div>
              <div className="input-summary-item" data-testid="summary-allowable-capacity">
                <span className="input-summary-label">Allowable (FS={results.factorOfSafety})</span>
                <span className="input-summary-value">
                  {results.allowableCapacity.toFixed(3)} MN
                </span>
              </div>
              <div className="input-summary-item" data-testid="summary-governing">
                <span className="input-summary-label">Governing</span>
                <span className="input-summary-value text-xs">
                  {results.governingComponent}
                </span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* FOS Summary */}
      {fos?.minFS && (
        <>
          <Separator />
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
              Factor of Safety
            </h3>
            <div className="space-y-1">
              <div className="input-summary-item" data-testid="summary-min-fs">
                <span className="input-summary-label">Min FS</span>
                <span className={`input-summary-value font-semibold ${
                  fos.minFS >= 1.5 ? 'text-green-600' : fos.minFS >= 1.2 ? 'text-amber-600' : 'text-red-600'
                }`}>
                  {fos.minFS.toFixed(2)}
                </span>
              </div>
              <div className="input-summary-item" data-testid="summary-governing-case">
                <span className="input-summary-label">Governing Case</span>
                <span className="input-summary-value text-xs">
                  {fos.governingCase}
                </span>
              </div>
              <div className="input-summary-item" data-testid="summary-fos-status">
                <span className="input-summary-label">Status</span>
                <span className={`input-summary-value text-xs font-semibold ${
                  getFOSStatus(fos.minFS) === 'PASS' ? 'text-green-600' : 
                  getFOSStatus(fos.minFS) === 'WARNING' ? 'text-amber-600' : 'text-red-600'
                }`}>
                  {getFOSStatus(fos.minFS)}
                </span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Sections breakdown */}
      {pileInputs.pileSections.length > 1 && (
        <>
          <Separator />
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
              Pile Sections ({pileInputs.pileSections.length})
            </h3>
            <div className="space-y-2">
              {pileInputs.pileSections.map((section, index) => (
                <div 
                  key={section.id} 
                  className="text-xs bg-muted/30 rounded p-2"
                  data-testid={`summary-section-${index}`}
                >
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Section {index + 1}</span>
                    <span className="font-mono">{section.diameter}×{section.wallThickness} mm</span>
                  </div>
                  <div className="flex justify-between mt-1">
                    <span className="text-muted-foreground">Length</span>
                    <span className="font-mono">{section.length} m</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
